import {
  BadgeCell,
  HtmlCell,
  ImageCell,
  ImageTextCell,
  TextCell,
} from "@/components/table/CustomCell";
import i18n, { SupportedLocale } from "@/i18n";
import {
  computeFieldCollectorName,
  computeFieldDate,
  computeFieldDescriptionEnsembleFixeArtwork,
  computeFieldDescriptionEnsembleTemporaireArtwork,
  computeFieldDimensionsArtwork,
  computeFieldDimensionsCadreArtwork,
  computeFieldDimensionsHorsMargesArtwork,
  computeFieldRelatedAuthorsArtwork,
  computeFieldUnstyledArtwork,
} from "@/lib/computeFields";
import {
  Artwork,
  ArtworkInAuction,
  ArtworkInBibliography,
  ArtworkInExhibition,
} from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { AdvancedSearchConfig } from "../forms/advanced-search/advanced-search-fields-def";
import { ColumnFormatted } from "./general";

export const artworkColumnsFormatted: ColumnFormatted[] = [
  {
    type: "string",
    key: "oeuvresDescriptionNumeroEnregistrement",
    visible: true,
  },
  {
    type: "image",
    key: "oeuvresDescriptionVignette",
    visible: true,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionEnsemble",
    visible: false,
    formattedData(data) {
      return computeFieldDescriptionEnsembleFixeArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresValidationAjoutUnEnsembleTemporaire",
    visible: false,
    formattedData(data) {
      return computeFieldDescriptionEnsembleTemporaireArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresDescriptionNomArtiste",
    visible: true,
    formattedData(data) {
      return computeFieldRelatedAuthorsArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "computed",
    key: "oeuvresDescriptionTitre",
    visible: true,
    formattedData(data) {
      return computeFieldUnstyledArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionTitreSansTitre",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionTitreInconnu",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresDescriptionDescriptionLongue",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSousTitre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAutreTitre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionReference",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDateDeCreation",
    visible: true,
    formattedData(data) {
      return computeFieldDate(
        {
          date: data.oeuvresDescriptionDateDeCreation,
          unknownDate: data.oeuvresDescriptionDateDeCreationInconnue,
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDateDeCreationInconnue",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionPeriodeCreation",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAireGeographique",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionEcoleMouvement",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionCategorie",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSerie",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionTheme",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionLieuDeCreation",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDimensionsOeuvre",
    visible: true,
    formattedData(data: Artwork) {
      return computeFieldDimensionsArtwork(
        {
          oeuvresDescriptionDimensionsOeuvreInconnues:
            data.oeuvresDescriptionDimensionsOeuvreInconnues,
          oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
            data.oeuvresDescriptionDimensionsOeuvre,
          ),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsOeuvreDouteuses",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresDescriptionDimensionsHorsMarges",
    visible: false,
    formattedData(data) {
      return computeFieldDimensionsHorsMargesArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesVerifiees",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesInconnues",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionDimensionsHorsMargesDouteuses",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionPoidsOeuvre",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresDescriptionOeuvreDoubleFace",
    visible: false,
  },
  {
    type: "badge",
    key: "oeuvresDescriptionTypeOeuvreMultilingual",
    visible: true,
    formattedData(data) {
      if (
        Object.keys(data.oeuvresDescriptionTypeOeuvreMultilingual).length === 0
      )
        return "";
      return (
        data.oeuvresDescriptionTypeOeuvreMultilingual?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "string",
    key: "oeuvresDescriptionTechniqueFRA",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionRemarqueSupportFRA",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionTechniqueGB",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSupportGB",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresDescriptionRemarqueSupportGB",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionRealisationFabrication",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSignatureLocalisationFRA",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSignatureTranscription",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionSignatureLocalisationGB",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionInscriptionsDos",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionLegendeDocumentAnnexe",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNomFichierNumerique",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNumeroDInventaire",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionReferenceCollectionActuelle",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionNumeroAutre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresDescriptionAjoutDeMotsCles",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationDescriptionOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationHistoriqueCollectionsOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationExpositionsOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationBibliographieOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationVentesOk",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationSelectionnerFiche",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValidationSource",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreDocumentaire",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreImportante",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationOeuvreMajeure",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationAttributionDouteuse",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresValidationFaux",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValidationNotesDeTravail",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValidationRemarquesPubliques",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValidationRemarquesPersonnelles",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresLocalisationHistoriqueCollection",
    visible: false,
    formattedData(data: any) {
      return data.oeuvresLocalisationHistoriqueCollection
        ? computeFieldCollectorName(
            data.oeuvresLocalisationHistoriqueCollection,
            {
              language: i18n.language as SupportedLocale,
            },
          )
        : "";
    },
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueCollectionCatalogue",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueCollectionNote",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueNom",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueVille",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueHistoriqueDates",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueLieuDeStockage",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueNotesRangement",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueMarquage",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresLocalisationHistoriqueDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresLocalisationHistoriqueNotes",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresRegieCadre",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresRegieDescriptionCadre",
    visible: false,
  },
  {
    type: "computed",
    key: "oeuvresRegieDimensionsCadre",
    visible: false,
    formattedData(data: Artwork) {
      return computeFieldDimensionsCadreArtwork(data, {
        language: i18n.language as SupportedLocale,
      });
    },
  },
  {
    type: "boolean",
    key: "oeuvresRegieCaisseTransport",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieDescriptionCaisse",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieDimensionsCaisse",
    visible: false,
  },

  {
    type: "string",
    key: "oeuvresRegiePoidsCaisse",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegiePoidsCaisseAvecOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresRegieNotesRegie",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresRegieNotesCondition",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresPhotosNotesPhotos",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoScanAFaire",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoOkWeb",
    visible: false,
  },
  {
    type: "boolean",
    key: "oeuvresPhotosPhotoHdOk",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresPhotosNotesPreparationPhotos",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsCreditOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsCreditPhotoOeuvre",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsNatureDroits",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsMontantDroits",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresCreditsRemarqueCreditsDroits",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationEstimation",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationDateEstimation",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationNotesEstimation",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationValeurAssurance",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresValorisationDateValeurAssurance",
    visible: false,
  },
  {
    type: "richtext",
    key: "oeuvresValorisationRemarqueValeurAssurance",
    visible: false,
  },
  {
    type: "string",
    key: "oeuvresStatus",
    visible: false,
  },
  {
    type: "status",
    key: "oeuvresPublicationStatus",
    visible: true,
  },
];

// Currently unused
export const artworkColumns = [
  "oeuvresDescriptionNumeroEnregistrement",
  "oeuvresDescriptionVignette",
  "oeuvresDescriptionTitre",
  "oeuvresDescriptionNomArtiste",
  "oeuvresDescriptionDimensionsOeuvre",
  "oeuvresDescriptionDimensionsHorsMarges",
  "oeuvresDescriptionTypeOeuvre",
  "oeuvresPublicationStatus",
  "sources",
  "oeuvresDescriptionNomArtisteAttribueA",
  "oeuvresDescriptionNomArtisteAnonyme",
  "oeuvresDescriptionNomArtisteDansLeGoutDe",
  "oeuvresDescriptionNomArtisteEntourageDe",
  "oeuvresDescriptionNomArtisteDApres",
  "oeuvresDescriptionNomArtisteAtelierDe",
  "oeuvresDescriptionTitreSansTitre",
  "oeuvresDescriptionTitreInconnu",
  "oeuvresDescriptionDescriptionLongue",
  "oeuvresDescriptionSousTitre",
  "oeuvresDescriptionAutreTitre",
  "oeuvresDescriptionReference",
  "oeuvresDescriptionDateDeCreation",
  "oeuvresDescriptionDateDeCreationInconnue",
  "oeuvresDescriptionPeriodeCreation",
  "oeuvresDescriptionAireGeographique",
  "oeuvresDescriptionEcoleMouvement",
  "oeuvresDescriptionCategorie",
  "oeuvresDescriptionSerie",
  "oeuvresDescriptionTheme",
  "oeuvresDescriptionLieuDeCreation",
  "oeuvresDescriptionDimensionsOeuvreVerifiees",
  "oeuvresdescriptiondimensionsprofondeur",
  "oeuvresDescriptionDimensionsOeuvreInconnues",
  "oeuvresDescriptionDimensionsOeuvreDouteuses",
  "oeuvresDescriptionDimensionsHorsMargesVerifiees",
  "oeuvresDescriptionDimensionsHorsMargesInconnues",
  "oeuvresDescriptionDimensionsHorsMargesDouteuses",
  "oeuvresDescriptionPoidsOeuvre",
  "oeuvresDescriptionOeuvreDoubleFace",
  "oeuvresDescriptionTechniqueFRA",
  "oeuvresDescriptionSupportFRA",
  "oeuvresDescriptionRemarqueSupportFRA",
  "oeuvresDescriptionTechniqueGB",
  "oeuvresDescriptionSupportGB",
  "oeuvresDescriptionRemarqueSupportGB",
  "oeuvresDescriptionRealisationFabrication",
  "oeuvresDescriptionSignatureLocalisationFRA",
  "oeuvresDescriptionSignatureTranscription",
  "oeuvresDescriptionSignatureLocalisationGB",
  "oeuvresDescriptionInscriptionsDos",
  "oeuvresDescriptionInscriptionsDosEN",
  "oeuvresDescriptionResume",
  "oeuvresDescriptionLegendeDocumentAnnexe",
  "oeuvresDescriptionNomFichierNumerique",
  "oeuvresDescriptionNumeroDInventaire",
  "oeuvresDescriptionReferenceCollectionActuelle",
  "oeuvresDescriptionNumeroAutre",
  "oeuvresDescriptionChampParametrableUtilisateur",
  "oeuvresDescriptionAjoutDeMotsCles",
  "oeuvresValidationSystemeDeNotationDeLoeuvre",
  "oeuvresValidationSystemeDePublicationDeLaFicheOnlineWebFront",
  "oeuvresValidationDescriptionOk",
  "oeuvresValidationHistoriqueCollectionsOk",
  "oeuvresValidationExpositionsOk",
  "oeuvresValidationBibliographieOk",
  "oeuvresValidationVentesOk",
  "oeuvresValidationSelectionnerFiche",
  "oeuvresValidationSource",
  "oeuvresValidationOeuvreDocumentaire",
  "oeuvresValidationOeuvreImportante",
  "oeuvresValidationOeuvreMajeure",
  "oeuvresValidationAttributionDouteuse",
  "oeuvresValidationFaux",
  "oeuvresValidationLost",
  "oeuvresValidationTitleNotConfirmed",
  "oeuvresValidationDestroy",
  "oeuvresDescriptionRestaurationNumerique",
  "oeuvresValidationStole",
  "oeuvresValidationNotesDeTravail",
  "oeuvresValidationRemarquesPubliques",
  "oeuvresValidationRemarquesPubliquesEN",
  "oeuvresValidationRemarquesPersonnelles",
  "oeuvresLocalisationHistoriqueCollectionCatalogue",
  "oeuvresLocalisationHistoriqueCollectionNote",
  "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
  "oeuvresLocalisationHistoriqueHistoriqueNom",
  "oeuvresLocalisationHistoriqueHistoriqueVille",
  "oeuvresLocalisationHistoriqueHistoriqueDates",
  "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
  "oeuvresLocalisationHistoriqueLieuDeStockage",
  "oeuvresLocalisationHistoriqueNotesRangement",
  "oeuvresLocalisationHistoriqueMarquage",
  "oeuvresLocalisationHistoriqueDepotAffectionPret",
  "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
  "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
  "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
  "oeuvresLocalisationHistoriqueNotes",
  "oeuvresRegieCadre",
  "oeuvresRegieDescriptionCadre",
  "oeuvresRegieDimensionsCadre",
  "oeuvresRegieCaisseTransport",
  "oeuvresRegieDescriptionCaisse",
  "oeuvresRegieDimensionsCaisse",
  "oeuvresRegiePoidsCaisse",
  "oeuvresRegiePoidsCaisseAvecOeuvre",
  "oeuvresRegiePhotoCaisse",
  "oeuvresRegieNotesRegie",
  "oeuvresRegieNotesCondition",
  "oeuvresPhotosNotesPhotos",
  "oeuvresPhotosPhotoScanAFaire",
  "oeuvresPhotosPhotoOkWeb",
  "oeuvresPhotosPhotoHdOk",
  "oeuvresPhotosNotesPreparationPhotos",
  "oeuvresCreditsCreditOeuvre",
  "oeuvresCreditsCreditPhotoOeuvre",
  "oeuvresCreditsNatureDroits",
  "oeuvresCreditsMontantDroits",
  "oeuvresCreditsRemarqueCreditsDroits",
  "oeuvresValorisationEstimation",
  "oeuvresValorisationDateEstimation",
  "oeuvresValorisationNotesEstimation",
  "oeuvresValorisationValeurAssurance",
  "oeuvresValorisationDateValeurAssurance",
  "oeuvresValorisationRemarqueValeurAssurance",
  "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
  "oeuvresDocumentsAdministratifsDocumentEdite",
  "oeuvresDocumentsAdministratifsLieuEtDateDocument",
  "oeuvresDescriptionTechniqueConfirmer",
  "oeuvresDescriptionSupportConfirmer",
  "oeuvresDescriptionNewBdOk",
  "oeuvresRangementRecolementMarquage",
  "oeuvresRangementRecolementRack",
  "oeuvresRangementRecolementPieceBox",
  "oeuvresRangementRecolementRangee",
  "oeuvresRangementRecolementMeuble",
  "oeuvresRangementRecolementEtagere",
  "oeuvresRangementRecolementBoiteCarton",
  "oeuvresRangementRecolementClasseur",
  "oeuvresRangementRecolementAutreReferenceRangement",
  "oeuvresRangementRecolementDateRangement",
  "oeuvresRangementRecolementDateMarquage",
  "oeuvresRangementRecolementOperateurRecolement",
  "oeuvresRangementRecolementDateRecolement",
  "oeuvresRangementRecolementReferenceCampagneRecolement",
  "oeuvresRangementRecolementAutreReferenceRecolement",
  "oeuvresRangementRecolementRecolementOk",
  "oeuvresAdminDocLettreInclusionEdit",
  "oeuvresAdminDocCertificatExist",
  "oeuvresRangementRecolementNotesRecolement",
  "oeuvresAdministratifNotes",
];

export const artworkColumnsVisibility = artworkColumnsFormatted.reduce(
  (acc, column) => ({
    ...acc,
    [column.key]: column.visible || false,
  }),
  {},
);

/*
 *
 * This is used to compute columns in CSV File for export
 *
 */
export const computedColumns: {
  [key: string]: (data: Artwork) => string;
} = {
  oeuvresDescriptionDimensionsOeuvre: (data) =>
    computeFieldDimensionsArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresRegieDimensionsCadre: (data) =>
    computeFieldDimensionsCadreArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresDescriptionDimensionsHorsMarges: (data) =>
    computeFieldDimensionsHorsMargesArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
  oeuvresDescriptionNomArtiste: (data) =>
    computeFieldRelatedAuthorsArtwork(data, {
      language: i18n.language as SupportedLocale,
    }),
};

// Currently unused
export const getArtworkColumns = (): ColumnDef<Artwork, any>[] => {
  return [
    {
      accessorKey: "oeuvresDescriptionNumeroEnregistrement",
      header: "N°",
      enableResizing: true,
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionVignette",
      header: "",
      size: 50,
      cell: (props) => {
        return (
          <ImageCell
            value={props.getValue()}
            attachments={props.row.original.attachments ?? []}
            domain="artwork"
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionTitre",
      header: "Titre",
      size: 200,
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionAutreTitre",
      header: "Autre titre",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionNomArtiste",
      header: "Artiste",
      cell: (props) => {
        return (
          <TextCell
            value={computeFieldRelatedAuthorsArtwork(props.row.original, {
              language: i18n.language as SupportedLocale,
            })}
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionTypeOeuvreMultilingual",
      header: "Type d'oeuvre",
      cell: (props) => {
        if (
          Object.keys(
            props.row.original.oeuvresDescriptionTypeOeuvreMultilingual,
          ).length === 0
        )
          return null;
        return (
          <BadgeCell
            value={
              props.row.original.oeuvresDescriptionTypeOeuvreMultilingual[
                i18n.language as SupportedLocale
              ] ?? ""
            }
            variant="outlined"
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDescriptionLongue",
      header: "Description longue",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionSousTitre",
      header: "Sous titre",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionReference",
      header: "Référence",
      cell: (props) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "oeuvresDescriptionDimensionsHorsMarges",
      header: "Dimensions hors marges",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(props.row.original, {
                language: i18n.language as SupportedLocale,
              })}
            />
          )
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDimensionsOeuvre",
      header: "Dimensions",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(props.row.original, {
                language: i18n.language as SupportedLocale,
              })}
            />
          )
        );
      },
    },
    {
      accessorKey: "oeuvresPublicationStatus",
      header: "Statut",
      size: 100,
      cell: (props) => {
        return (
          <BadgeCell
            value={props.getValue() === "published" ? "Publié" : "Brouillon"}
            variant={
              props.row.original.oeuvresPublicationStatus === "published"
                ? "published"
                : "draft"
            }
          />
        );
      },
    },
    {
      accessorKey: "oeuvresDescriptionDateDeCreation",
      header: "Date de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionPeriodeCreation",
      header: "Période de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionAireGeographique",
      header: "Aire géographique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionEcoleMouvement",
      header: "École/Mouvement",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionCategorie",
      header: "Catégorie",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSerie",
      header: "Série",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionTheme",
      header: "Thème",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionLieuDeCreation",
      header: "Lieu de création",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionTechniqueFRA",
      header: "Technique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSupportFRA",
      header: "Support",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionPoidsOeuvre",
      header: "Poids",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSignatureLocalisationFRA",
      header: "Localisation signature",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionSignatureTranscription",
      header: "Transcription signature",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionInscriptionsDos",
      header: "Inscriptions dos",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionResume",
      header: "Résumé",
      size: 200,
      cell: (props) => <HtmlCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresDescriptionNumeroDInventaire",
      header: "N° Inventaire",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationSystemeDeNotationDeLoeuvre",
      header: "Notation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationRemarquesPubliques",
      header: "Remarques publiques",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueLieuDeStockage",
      header: "Lieu de stockage",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValidationDescriptionOk",
      header: "Description validée",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationHistoriqueCollectionsOk",
      header: "Historique validé",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationExpositionsOk",
      header: "Expositions validées",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationBibliographieOk",
      header: "Bibliographie validée",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationVentesOk",
      header: "Ventes validées",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreDocumentaire",
      header: "Œuvre documentaire",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreImportante",
      header: "Œuvre importante",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationOeuvreMajeure",
      header: "Œuvre majeure",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresValidationAttributionDouteuse",
      header: "Attribution douteuse",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationFaux",
      header: "Faux",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationLost",
      header: "Perdue",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationStole",
      header: "Volée",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresValidationDestroy",
      header: "Détruite",
      cell: (props) => (
        <BadgeCell value={props.getValue() ? "Oui" : "Non"} variant="draft" />
      ),
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueNom",
      header: "Nom historique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueVille",
      header: "Ville historique",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresLocalisationHistoriqueHistoriqueDates",
      header: "Dates historiques",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRegieCadre",
      header: "Cadre",
      cell: (props) => (
        <BadgeCell
          value={props.getValue() ? "Oui" : "Non"}
          variant={props.getValue() ? "published" : "draft"}
        />
      ),
    },
    {
      accessorKey: "oeuvresRegieDescriptionCadre",
      header: "Description cadre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRegieDimensionsCadre",
      header: "Dimensions cadre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsCreditOeuvre",
      header: "Crédit œuvre",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsCreditPhotoOeuvre",
      header: "Crédit photo",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresCreditsNatureDroits",
      header: "Nature des droits",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValorisationEstimation",
      header: "Estimation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValorisationDateEstimation",
      header: "Date estimation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresValorisationValeurAssurance",
      header: "Valeur assurance",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementRack",
      header: "Rack",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementPieceBox",
      header: "Box",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementMeuble",
      header: "Meuble",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "oeuvresRangementRecolementEtagere",
      header: "Étagère",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
  ];
};

export const getArtworkForComplexLink = (): ColumnDef<
  ArtworkInBibliography | ArtworkInExhibition | ArtworkInAuction,
  any
>[] => {
  return [
    {
      accessorKey: "artwork.oeuvresDescriptionNumeroEnregistrement",
      header: "oeuvresDescriptionNumeroEnregistrement",
      enableResizing: true,
      cell: (props: any) => {
        return <TextCell value={props.getValue()} />;
      },
    },
    {
      accessorKey: "vignette",
      header: "",
      size: 100,
      cell: (props: any) => {
        return (
          <ImageCell
            value={""}
            attachments={props.row.original.attachments ?? []}
            domain="artwork"
          />
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionTitre",
      header: "oeuvresDescriptionTitre",
      size: 200,
      cell: (props: any) => {
        return (
          <ImageTextCell
            value={props.getValue()}
            attachments={props.row.original.attachments ?? []}
            domain="artwork"
          />
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionDateDeCreation",
      header: "oeuvresDescriptionDateDeCreation",
      cell: (props) => <TextCell value={props.getValue()} />,
    },
    {
      accessorKey: "artwork.oeuvresDescriptionDimensionsOeuvre",
      header: "oeuvresDescriptionDimensionsOeuvre",
      cell: (props) => {
        return (
          props.row.original && (
            <HtmlCell
              value={computeFieldDimensionsArtwork(
                {
                  oeuvresDescriptionDimensionsOeuvreInconnues:
                    props.row.original.artwork
                      .oeuvresDescriptionDimensionsOeuvreInconnues,
                  oeuvresDescriptionDimensionsOeuvre: parseJsonOrUndefined(
                    props.row.original.artwork
                      .oeuvresDescriptionDimensionsOeuvre,
                  ),
                },
                {
                  language: i18n.language as SupportedLocale,
                },
              )}
            />
          )
        );
      },
    },
    {
      accessorKey: "artwork.oeuvresDescriptionTypeOeuvreMultilingual",
      header: "oeuvresDescriptionTypeOeuvre",
      cell: (props: any) => {
        if (
          Object.keys(
            props.row.original?.artwork
              ?.oeuvresDescriptionTypeOeuvreMultilingual,
          ).length === 0
        )
          return null;
        return (
          <BadgeCell
            value={
              props.row.original?.artwork
                ?.oeuvresDescriptionTypeOeuvreMultilingual[
                i18n.language as SupportedLocale
              ] ?? ""
            }
            variant="outlined"
          />
        );
      },
    },
  ];
};

export const artworkAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "artworksGeneralInfo",
    fields: [
      { type: "string", key: "oeuvresDescriptionNumeroEnregistrement" },
      { type: "string", key: "oeuvresDescriptionReferenceCollectionActuelle" },
      { type: "string", key: "oeuvresDescriptionTitre" },
      { type: "boolean", key: "oeuvresPhotosPhotoScanAFaire" },
      { type: "boolean", key: "oeuvresPhotosPhotoOkWeb" },
      { type: "boolean", key: "oeuvresPhotosPhotoHdOk" },
      { type: "string", key: "oeuvresDescriptionResume" },
      { type: "string", key: "oeuvresDescriptionNumeroAutre" },
      { type: "number", key: "oeuvresValidationSystemeDeNotationDeLoeuvre" },
      { type: "boolean", key: "oeuvresValidationDescriptionOk" },
      { type: "boolean", key: "oeuvresValidationHistoriqueCollectionsOk" },
      { type: "boolean", key: "oeuvresValidationExpositionsOk" },
      { type: "boolean", key: "oeuvresValidationBibliographieOk" },
      { type: "boolean", key: "oeuvresValidationVentesOk" },
      { type: "boolean", key: "oeuvresValidationSelectionnerFiche" },
      { type: "string", key: "oeuvresValidationSource" },
      { type: "boolean", key: "oeuvresValidationOeuvreDocumentaire" },
      { type: "boolean", key: "oeuvresValidationOeuvreImportante" },
      { type: "boolean", key: "oeuvresWaitingPublication" },
      { type: "boolean", key: "oeuvresNotPublished" },
      { type: "boolean", key: "oeuvresValidationOeuvreMajeure" },
      { type: "boolean", key: "oeuvresValidationAttributionDouteuse" },
      { type: "boolean", key: "oeuvresValidationFaux" },
      { type: "boolean", key: "oeuvresValidationLost" },
      { type: "boolean", key: "oeuvresValidationDestroy" },
      { type: "boolean", key: "oeuvresDescriptionRestaurationNumerique" },
      { type: "boolean", key: "oeuvresValidationStole" },
      { type: "string", key: "oeuvresValidationNotesDeTravail" },
      { type: "string", key: "oeuvresDescriptionAjoutDeMotsCles" },
      { type: "string", key: "oeuvresDescriptionNumeroDInventaire" },
      { type: "string", key: "oeuvresStatus" },
    ],
  },
  {
    type: "group",
    label: "artworksDescription",
    fields: [
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAttribueA" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAnonyme" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteDansLeGoutDe" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteEntourageDe" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteDApres" },
      { type: "boolean", key: "oeuvresDescriptionNomArtisteAtelierDe" },
      { type: "boolean", key: "oeuvresDescriptionTitreSansTitre" },
      { type: "boolean", key: "oeuvresDescriptionTitreInconnu" },
      { type: "boolean", key: "oeuvresValidationTitleNotConfirmed" },
      { type: "string", key: "oeuvresDescriptionDescriptionLongue" },
      { type: "string", key: "oeuvresDescriptionSousTitre" },
      { type: "string", key: "oeuvresDescriptionAutreTitre" },
      { type: "string", key: "oeuvresDescriptionReference" },
      { type: "string", key: "oeuvresDescriptionDateDeCreation" },
      { type: "boolean", key: "oeuvresDescriptionDateDeCreationInconnue" },
      { type: "string", key: "oeuvresDescriptionPeriodeCreation" },
      { type: "string", key: "oeuvresDescriptionAireGeographique" },
      { type: "string", key: "oeuvresDescriptionEcoleMouvement" },
      { type: "string", key: "oeuvresDescriptionCategorie" },
      { type: "string", key: "oeuvresDescriptionSerie" },
      { type: "string", key: "oeuvresDescriptionTheme" },
      { type: "string", key: "oeuvresDescriptionLieuDeCreation" },
      { type: "string", key: "oeuvresDescriptionPoidsOeuvre" },
      { type: "boolean", key: "oeuvresDescriptionOeuvreDoubleFace" },
      { type: "multilingual", key: "oeuvresDescriptionTypeOeuvreMultilingual" },
      { type: "boolean", key: "oeuvresDescriptionTechniqueConfirmer" },
      { type: "boolean", key: "oeuvresDescriptionSupportConfirmer" },
      { type: "string", key: "oeuvresDescriptionRealisationFabrication" },
      { type: "string", key: "oeuvresDescriptionSignatureTranscription" },
      { type: "string", key: "oeuvresDescriptionNomFichierNumerique" },
      { type: "boolean", key: "oeuvresDescriptionRestaurationNumerique" },
      {
        type: "multilingual",
        key: "oeuvresDescriptionInscriptionsDosMultilingual",
      },
      { type: "multilingual", key: "oeuvresDescriptionRemarqueSupport" },
      { type: "multilingual", key: "oeuvresDescriptionSignatureLocalisation" },
      { type: "multilingual", key: "oeuvresDescriptionSupport" },
      { type: "multilingual", key: "oeuvresDescriptionTechnique" },
    ],
  },
  {
    type: "group",
    label: "artworkAuteurInfo",
    fields: [
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPrenom",
        label: "Prenom [Artiste]",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNom",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionEtatCivil",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionDateNaissance",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionVilleDeNaissance",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionDateDeDeces",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionVilleDeDeces",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNationalite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionResumeDates",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPaysDActivite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionActivite",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionPeriodeDactivit",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNotesDeTravail",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursDescriptionNotesBiographiques",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsNom",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsContact",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsLienAvecLAuteur",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursAyantDroitsOrganismeGerantLesDroits",
      },
      { type: "string", key: "relatedAuthor.some.artistesAuteursExpertCrNom" },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrContact",
      },
      {
        type: "boolean",
        key: "relatedAuthor.some.artistesAuteursExpertCrCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrEtatDuCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursExpertCrDescriptionDuCatalogueRaisonne",
      },
      {
        type: "string",
        key: "relatedAuthor.some.artistesAuteursPublicationStatus",
      },
      { type: "string", key: "relatedAuthor.some.sources" },
    ],
  },
  {
    type: "group",
    label: "artworksRangementRecolement",
    fields: [
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueLieuDeStockage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementPieceBox",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementRangee",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementRack",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementMeuble",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementEtagere",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementBoiteCarton",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementClasseur",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementAutreReferenceRangement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateRangement",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNotesRangement",
      },
      {
        type: "boolean",
        key: "oeuvresRangementRecolementMarquage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateMarquage",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementOperateurRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementDateRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementReferenceCampagneRecolement",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementAutreReferenceRecolement",
      },
      {
        type: "boolean",
        key: "oeuvresRangementRecolementRecolementOk",
      },
      {
        type: "string",
        key: "oeuvresRangementRecolementNotesRecolement",
      },
    ],
  },
  {
    type: "group",
    label: "artworksRegie",
    fields: [
      { type: "boolean", key: "oeuvresRegieCadre" },
      { type: "string", key: "oeuvresRegieDescriptionCadre" },
      { type: "string", key: "oeuvresRegieDimensionsCadre" },
      { type: "boolean", key: "oeuvresRegieCaisseTransport" },
      { type: "string", key: "oeuvresRegieDescriptionCaisse" },
      { type: "string", key: "oeuvresRegieDimensionsCaisse" },
      { type: "string", key: "oeuvresRegiePoidsCaisse" },
      { type: "string", key: "oeuvresRegiePoidsCaisseAvecOeuvre" },
      { type: "string", key: "oeuvresRegieNotesRegie" },
      { type: "string", key: "oeuvresRegieNotesCondition" },
    ],
  },
  {
    type: "group",
    label: "artworksPhotosCrédits",
    fields: [
      { type: "string", key: "oeuvresPhotosNotesPhotos" },
      { type: "string", key: "oeuvresPhotosNotesPreparationPhotos" },
      { type: "string", key: "oeuvresCreditsCreditOeuvre" },
      { type: "string", key: "oeuvresCreditsCreditPhotoOeuvre" },
      { type: "string", key: "oeuvresCreditsNatureDroits" },
      { type: "string", key: "oeuvresCreditsMontantDroits" },
      { type: "string", key: "oeuvresCreditsRemarqueCreditsDroits" },
    ],
  },
  {
    type: "group",
    label: "artworksValidationsDocumentsAdministratifs",
    fields: [
      {
        type: "string",
        key: "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
      },
      { type: "boolean", key: "oeuvresDocumentsAdministratifsDocumentEdite" },
      {
        type: "string",
        key: "oeuvresDocumentsAdministratifsLieuEtDateDocument",
      },
      { type: "boolean", key: "oeuvresAdminDocLettreInclusionEdit" },
      { type: "boolean", key: "oeuvresAdminDocCertificatExist" },
      { type: "string", key: "oeuvresAdministratifNotes" },
    ],
  },
  {
    type: "group",
    label: "artworksLocalisationHistorique",
    fields: [
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueCollectionCatalogue",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueCollectionNote",
      },
      {
        type: "boolean",
        key: "oeuvresLocalisationHistoriqueOeuvreNonLocalisee",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueHistoriqueNom",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueHistoriqueVille",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueHistoriqueDates",
      },
      {
        type: "boolean",
        key: "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNotes",
      },
      {
        type: "boolean",
        key: "oeuvresLocalisationHistoriqueDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueDateDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret",
      },
      {
        type: "string",
        key: "oeuvresLocalisationHistoriqueNoteDepotAffectionPret",
      },
      {
        type: "string",
        label: "oeuvresLocalisationHistoriqueCollection",
        key: "oeuvresLocalisationHistoriqueCollection.collectionneursDescriptionNom",
      },
    ],
  },
  {
    type: "group",
    label: "sources",
    fields: [{ type: "string", key: "sources" }],
  },
  {
    type: "group",
    label: "notes",
    fields: [
      { type: "string", key: "oeuvresValidationNotesDeTravail" },
      { type: "string", key: "oeuvresValidationRemarquesPersonnelles" },
      {
        type: "multilingual",
        key: "oeuvresValidationRemarquesPubliquesMultilingual",
      },
    ],
  },
  {
    type: "group",
    label: "artworksSuiviFinancier",
    fields: [
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.estimate.title",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.estimate.date",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.estimate.amount",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.estimate.currency",
        additionalQuery: [{ category: { equals: "estimate" } }],
      },
      {
        type: "string",
        label: "suiviFinancier.disposition.title",
        key: "oeuvreVenteTitre",
      },
      {
        type: "string",
        label: "suiviFinancier.disposition.date",
        key: "oeuvreVenteDate",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amount",
        key: "oeuvreVenteMontant",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amountTVA",
        key: "oeuvreVenteMontantTVA",
      },
      {
        type: "currency",
        label: "suiviFinancier.disposition.amountTOP",
        key: "oeuvreVenteMontantTOP",
      },
      {
        type: "string",
        label: "suiviFinancier.acquisition.title",
        key: "oeuvreAchatTitre",
      },
      {
        type: "string",
        label: "suiviFinancier.acquisition.date",
        key: "oeuvreAchatDate",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amount",
        key: "oeuvreAchatMontant",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountTVA",
        key: "oeuvreAchatMontantTVA",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountDroitsDeDouane",
        key: "oeuvreAchatMontantDroitsDeDouane",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountDroitsDeSuite",
        key: "oeuvreAchatMontantDroitsDeSuite",
      },
      {
        type: "currency",
        label: "suiviFinancier.acquisition.amountFraisDeVente",
        key: "oeuvreAchatMontantFraisDeVente",
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.production.title",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.production.date",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.production.amount",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.production.currency",
        additionalQuery: [{ category: { equals: "production" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.transport.title",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.transport.date",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.transport.amount",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.transport.currency",
        additionalQuery: [{ category: { equals: "transport" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.restoration.title",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.restoration.date",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.restoration.amount",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.restoration.currency",
        additionalQuery: [{ category: { equals: "restoration" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.framing.title",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.framing.date",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.framing.amount",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.framing.currency",
        additionalQuery: [{ category: { equals: "framing" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.photography.title",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.photography.date",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.photography.amount",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.photography.currency",
        additionalQuery: [{ category: { equals: "photography" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.expertise.title",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.expertise.date",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.expertise.amount",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.expertise.currency",
        additionalQuery: [{ category: { equals: "expertise" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.title",
        label: "suiviFinancier.insurance.title",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.date",
        label: "suiviFinancier.insurance.date",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "number",
        multiplyBy: 100,
        key: "MonetaryTransaction.some.amount",
        label: "suiviFinancier.insurance.amount",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      {
        type: "string",
        key: "MonetaryTransaction.some.currency",
        label: "suiviFinancier.insurance.currency",
        additionalQuery: [{ category: { equals: "insurance" } }],
      },
      { type: "string", key: "oeuvreSuiviFinancierNotes" },
    ],
  },
];
